import type {
  CustomerDTO,
  SveaCreateOrderModel,
  SveaOrder,
  SveaOrderRow,
  SveaPresetValue
} from 'ecosystem'
import { CustomerType } from 'ecosystem'
import { sveaApi } from 'api'
import {
  createMinorUnit,
  DEFAULT_COUNTRY,
  DEFAULT_CURRENCY,
  getFullLocale,
  normalizeCountryCode,
  resolveBuyerCountry,
  resolveUrlPath
} from 'shared-utils'
import { sveaOrderLineFactory } from 'api/utils/svea/checkout'
import { runCreateOrder } from './runCreateOrder'
import {
  type BuildPayloadArgs,
  type CreateOrderContext,
  type CreateOrderHandlerOpts,
  type CreateOrderRouteResponse,
  type PaymentProviderAdapter
} from './types'

const getSveaFullLocale = (locale: BuildPayloadArgs['ctx']['locale']) =>
  getFullLocale(locale, { no: 'nn-NO', en: 'en-US' })

/**
 * Markets where presetting the organisation number is known to work.
 *
 * Svea runs a per-country identity flow and only Sweden's accepts an
 * organisation number as a preset value. Poland rejects every value tried — a
 * Swedish orgnr, and checksum-valid Polish NIPs alike — failing the whole
 * create-order call with an empty-bodied 500 rather than a field error.
 *
 * This gates the number only. `IsCompany` is safe everywhere and is still
 * preset for business customers, so a B2B buyer lands in the company flow and
 * fills in their own local identifier there.
 *
 * Add a country here only once a B2B order has actually been placed in it.
 */
const ORG_NUMBER_PRESET_COUNTRIES = ['SE']

/**
 * The country the stored customer data belongs to, resolved the same way the
 * postal code below is: the customer's own addresses first, then the company's.
 */
const resolveCustomerCountry = (customer: CustomerDTO): string | undefined => {
  const customerAddresses = customer.addresses ?? []
  const country =
    customerAddresses.find((a) => a.main && a.country)?.country ??
    customerAddresses.find((a) => a.country)?.country ??
    resolveBuyerCountry(customer.companyProfile)

  return country ? normalizeCountryCode(country) : undefined
}

// Lookup order: customer.addresses (main first) -> companyProfile.addresses
// (default billing, then other billing, then shipping).
const resolveCustomerPostalCode = (customer: CustomerDTO): string | undefined => {
  const customerAddresses = customer.addresses ?? []
  const mainCustomerAddress = customerAddresses.find((a) => a.main && a.postalCode)
  if (mainCustomerAddress?.postalCode) return mainCustomerAddress.postalCode
  const anyCustomerAddress = customerAddresses.find((a) => a.postalCode)
  if (anyCustomerAddress?.postalCode) return anyCustomerAddress.postalCode

  const companyAddresses = customer.companyProfile?.addresses ?? []
  const billing = companyAddresses.filter((a) => a.type === 'BILLING' && a.postalCode)
  const defaultBilling = billing.find((a) => a.default)
  if (defaultBilling?.postalCode) return defaultBilling.postalCode
  if (billing[0]?.postalCode) return billing[0].postalCode

  const shipping = companyAddresses.find((a) => a.type === 'SHIPPING' && a.postalCode)
  return shipping?.postalCode
}

/**
 * Prefill what Svea will accept for the signed-in shopper.
 *
 * The customer comes off the context — the route entry already looked it up to
 * resolve the buyer's VAT treatment, and fetching the same record again here is
 * what the pages version did. A null customer (guest, or a failed look-up) just
 * means no presets: the shopper fills the form in themselves.
 */
const sveaPresetValues = (
  customer: CustomerDTO | null,
  sessionCountry: string | undefined
): SveaPresetValue[] => {
  if (!customer) {
    return []
  }

  const presetValues: SveaPresetValue[] = []

  // Prefill email — non-readonly so the shopper can still change it
  // (they may be ordering on someone else's behalf).
  if (customer.email) {
    presetValues.push({ TypeName: 'EmailAddress', Value: customer.email })
  }

  if (customer.phones[0]?.number) {
    presetValues.push({ TypeName: 'PhoneNumber', Value: customer.phones[0].number })
  }

  // Svea runs a per-country identity flow and validates presets against the
  // session's country, so stored data only goes in when it belongs to that
  // country. Email and phone are country-neutral and always sent.
  const customerCountry = resolveCustomerCountry(customer)
  const presetsMatchSessionCountry =
    !!customerCountry &&
    !!sessionCountry &&
    customerCountry === normalizeCountryCode(sessionCountry)

  if (presetsMatchSessionCountry) {
    const postalCode = resolveCustomerPostalCode(customer)
    if (postalCode) {
      presetValues.push({ TypeName: 'PostalCode', Value: postalCode })
    }
  }

  if (customer.type === CustomerType.BUSINESS) {
    // Safe in every market: it only opens the company flow, whose identifier
    // field the shopper can still fill in themselves.
    presetValues.push({ TypeName: 'IsCompany', Value: 'True', IsReadonly: true })

    // For B2B in Svea SCO, `NationalId` is the organisation number (Svea reuses
    // the same preset field for both consumer SSN and company orgnr;
    // `IsCompany=true` switches the meaning).
    const orgNumber = customer.companyProfile?.organizationNumber
    const canPresetOrgNumber =
      presetsMatchSessionCountry &&
      !!customerCountry &&
      ORG_NUMBER_PRESET_COUNTRIES.includes(customerCountry)

    if (orgNumber && canPresetOrgNumber) {
      presetValues.push({ TypeName: 'NationalId', Value: orgNumber })
    }
  }

  return presetValues
}

const buildSveaPayload = ({
  ctx,
  opts,
  backendOrder,
  sessionCountry,
  configCountry,
  shippingInfo
}: BuildPayloadArgs): SveaCreateOrderModel => {
  const sveaOptions = opts.paymentProviderConfig?.providerOptions?.svea
  const { items, cartDiscountCode, cartStateUuid } = ctx.payload
  const { orderLines } = sveaOrderLineFactory(items, cartDiscountCode, configCountry, opts.buyerVat)
  const presetValues = sveaPresetValues(ctx.customer ?? null, sessionCountry)

  // Svea's ConfirmationUri does NOT support a template variable (only PushUri does, with
  // `{checkout.order.uri}` → Svea OrderId). To still get an identifier on the redirect back, we
  // hardcode the SF orderNumber. The confirmation page reads it as `order_id` and the SF order's
  // persisted `paymentId` (set by `persistPaymentId`) provides the link back to the Svea OrderId.
  const confirmationUri = `${resolveUrlPath(opts.redirectDomain, '/confirmation')}?order_id=${encodeURIComponent(backendOrder.orderNumber ?? '')}`

  return {
    locale: getSveaFullLocale(ctx.locale),
    countryCode: sessionCountry || configCountry || DEFAULT_COUNTRY,
    currency: sveaOptions?.purchaseCurrency || DEFAULT_CURRENCY,
    clientOrderNumber: backendOrder.orderNumber ?? '',
    merchantSettings: {
      TermsUri: resolveUrlPath(opts.redirectDomain, sveaOptions?.termsUrl ?? '/terms'),
      CheckoutUri: resolveUrlPath(opts.redirectDomain, '/checkout'),
      ConfirmationUri: confirmationUri,
      PushUri: resolveUrlPath(opts.redirectDomain, '/api/orders/push?order_id={checkout.order.uri}')
    },
    cart: {
      Items: shippingInfo
        ? [
            ...orderLines,
            {
              ArticleNumber: shippingInfo.id,
              Name: shippingInfo.name,
              Quantity: createMinorUnit(1),
              Unit: 'NA',
              UnitPrice: shippingInfo.price,
              VatPercent: shippingInfo.tax_rate,
              RowType: 'ShippingFee'
            } satisfies SveaOrderRow
          ]
        : orderLines
    },
    merchantData: JSON.stringify({ cartStateUuid }),
    partnerKey: process.env.SVEA_PARTNER_KEY ?? '',
    ...(presetValues.length > 0 && { presetValues })
  } satisfies SveaCreateOrderModel
}

/**
 * The session country is also the identity flow Svea renders, so it is the
 * delivery country the DEFAULT shipping rules are evaluated against.
 * @see https://paymentsdocs.svea.com/docs/checkout/checkout-business-flows/
 */
const sveaAdapter: PaymentProviderAdapter<SveaOrder, SveaCreateOrderModel> = {
  name: 'SVEA',
  configCountry: (opts) => opts.paymentProviderConfig?.providerOptions?.svea?.countryCode,
  availableCountries: (opts) =>
    opts.paymentProviderConfig?.providerOptions?.svea?.availableCountries,
  orderLineFactory: sveaOrderLineFactory,
  buildPayload: buildSveaPayload,
  createSession: (payload) => sveaApi.checkout.createOrder(payload),
  respond: (sveaResponse) => ({ sveaResponse }),
  paymentIdOf: (session) => String(session.OrderId)
}

export const sveaPaymentProvider = (
  ctx: CreateOrderContext,
  opts: CreateOrderHandlerOpts
): Promise<CreateOrderRouteResponse> => runCreateOrder(ctx, opts, sveaAdapter)
